import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Scale, FileText, Shield, Phone, Mail, MapPin, CheckCircle2, ArrowRight } from 'lucide-react';
import { mockData } from '../mock';

const Home = () => {
  const { firm, practiceAreas } = mockData;
  
  const iconMap = {
    FileText: FileText,
    Scale: Scale,
    Shield: Shield
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-navy-900 to-slate-800 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              {firm.name}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-slate-300">
              {firm.tagline}
            </p>
            <p className="text-lg mb-10 text-slate-400 leading-relaxed">
              Expert legal representation in Tax, Bankruptcy, and Personal Injury law. 
              We're dedicated to protecting your rights and achieving the best possible outcomes.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                  Schedule Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/practice-areas">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-navy-900 px-8 py-6 text-lg transition-all duration-300">
                  Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-navy-900 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-amber-500" />
              </div>
              <h3 className="text-2xl font-bold text-navy-900 mb-2">Trusted Expertise</h3>
              <p className="text-slate-600">Years of experience serving clients</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-navy-900 rounded-full flex items-center justify-center mb-4">
                <Scale className="h-8 w-8 text-amber-500" />
              </div>
              <h3 className="text-2xl font-bold text-navy-900 mb-2">Results-Driven</h3>
              <p className="text-slate-600">Committed to favorable outcomes</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-navy-900 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-8 w-8 text-amber-500" />
              </div>
              <h3 className="text-2xl font-bold text-navy-900 mb-2">Client-Focused</h3>
              <p className="text-slate-600">Your success is our priority</p>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Practice Areas</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comprehensive legal services tailored to your needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {practiceAreas.map((area) => {
              const IconComponent = iconMap[area.icon];
              return (
                <Card key={area.id} className="border-2 border-slate-200 hover:border-navy-900 transition-all duration-300 hover:shadow-xl group">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-navy-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-navy-900 transition-colors duration-300">
                      <IconComponent className="h-8 w-8 text-navy-900 group-hover:text-amber-500 transition-colors duration-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-navy-900 mb-4">{area.title}</h3>
                    <p className="text-slate-600 mb-6 leading-relaxed">{area.description}</p>
                    <Link to="/practice-areas">
                      <Button variant="ghost" className="text-navy-900 hover:text-amber-600 p-0 h-auto font-semibold">
                        Learn More <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Schedule a consultation today and let us help you navigate your legal challenges with confidence.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-6 text-lg transition-all duration-300 hover:scale-105">
              Book Your Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Contact Info Bar */}
      <section className="py-8 bg-slate-100">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-8 text-navy-900">
            <div className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-amber-600" />
              <span className="font-semibold">{firm.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-amber-600" />
              <span className="font-semibold">{firm.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-amber-600" />
              <span className="font-semibold">{firm.address}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;